import classNames from 'classnames/bind';
import styles from './InvoiceListing.module.scss';
import TableClassicReport from '../base/TableClassicReport';
import { useEffect, useState } from 'react';
import { IInvoiceListingRecord, IPaginationReport } from '~/utils/interface/report';
import reportApi from '~/apis/report';
import { ColumnType } from '~/utils/types/common';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

const InvoiceListing = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IInvoiceListingRecord[]>([]);
  const [pagination, setPagination] = useState<IPaginationReport>({
    totalItems: 0,
    totalPage: 0,
    page: 1,
    limit: 10,
    skip: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await reportApi.getInvoiceListing({ page: pagination.page });
        setData(response.data?.data?.responses ?? []);
        setPagination(response.data?.data?.pagination ?? {
          totalItems: 0,
          totalPage: 0,
          page: 1,
          limit: 10,
          skip: 0,
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch invoice listings', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [pagination.page]);


  const columns: Array<ColumnType> = [
    {
      id: "status",
      labelHeader: "Status",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.status || "-"}</p>
        </div>
      ),
    },
    {
      id: "number",
      labelHeader: "Number",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.uniqueCode || "-"}</p>
        </div>
      ),
    },
    {
      id: "subTotal",
      labelHeader: "Sub Total",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{`$${invoice.total || "0"}`}</p>
        </div>
      ),
    },
    {
      id: "tax",
      labelHeader: "Tax",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{`$0.00`}</p>
        </div>
      ),
    },
    {
      id: "total",
      labelHeader: "Total",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{`$${invoice.total || "0"}`}</p>
        </div>
      ),
    },
    {
      id: "totalPaid",
      labelHeader: "Total Paid",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{`$${invoice.paid || "0"}`}</p>
        </div>
      ),
    },
    {
      id: "balance",
      labelHeader: "Balance",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{`$${Number(Number(invoice.total) - Number(invoice.paid) || 0)}`}</p>
        </div>
      ),
    },
    {
      id: "createdByAccount",
      labelHeader: "Created By",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.createdByName ?? "-"}</p>
        </div>
      ),
    },
    {
      id: "relatedRecords",
      labelHeader: "Related Records",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.relatedContact.name ? `${invoice.relatedContact.name} (Contact)` : "-"}</p>
          <p className={cx("text-content")}>{invoice.uniqueCode ? `${invoice.uniqueCode} (Job)` : "-"}</p>
        </div>
      ),
    },
    {
      id: "dateUpdated",
      labelHeader: "Date Updated",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{dayjs(invoice.dateUpdated).format("MM/DD/YYYY") || "-"}</p>
        </div>
      ),
    },
    {
      id: "dateInvoice",
      labelHeader: "Date Invoice",
      renderCell: (invoice: IInvoiceListingRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{dayjs(invoice.dateInvoiced).format("MM/DD/YYYY") || "-"}</p>
        </div>
      ),
    },
  ];

  return (
    <TableClassicReport
      title="Invoice Listing"
      columns={columns}
      data={data}
      page={pagination.page}
      totalPages={pagination.totalPage}
      totalItems={pagination.totalItems}
      itemsPerPage={pagination.limit}
      onPageChange={(page) => {
        setPagination({ ...pagination, page });
      }}
      isLoading={loading}
    />
  );
};

export default InvoiceListing;
